import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsNone';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';

/**
 * The in-app notification feed.
 *
 * This is the channel that always works: flight and gate alerts also go out by
 * email, but email can be filtered or delayed, and someone standing at a gate
 * needs to be able to open the app and see why we pinged them.
 *
 * Opening the menu does not mark everything read — a traveller who glances at the
 * badge and closes it again has not dealt with anything. Reading is either
 * explicit ("mark all read") or implied by acting on a single notification.
 */

const POLL_INTERVAL_MS = 2 * 60 * 1000;

const NotificationBell = ({ enabled = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    if (!enabled) return;
    try {
      const response = await api.get('/notifications/unread-count');
      setUnread(response.data.unread || 0);
    } catch (err) {
      // A failing badge is not worth interrupting anyone over.
    }
  }, [enabled]);

  useEffect(() => {
    fetchUnreadCount();
    if (!enabled) return undefined;

    const timer = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, fetchUnreadCount]);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);

    try {
      const response = await api.get('/notifications', { params: { limit: 20 } });
      setNotifications(response.data.notifications || []);
      setUnread(response.data.unread || 0);
    } catch (err) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const response = await api.post('/notifications/mark-read', {});
      setUnread(response.data.unread || 0);
      setNotifications((current) =>
        current.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() }))
      );
    } catch (err) {
      // Leave the badge as it was; a retry is one click away.
    }
  };

  const handleOpenNotification = async (notification) => {
    setAnchorEl(null);

    if (!notification.read_at) {
      try {
        const response = await api.post('/notifications/mark-read', { ids: [notification.id] });
        setUnread(response.data.unread || 0);
      } catch (err) {
        // Navigation matters more than the badge.
      }
    }

    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  if (!enabled) return null;

  return (
    <>
      <IconButton
        aria-label={t('notifications.title')}
        onClick={handleOpen}
        sx={{
          color: 'white',
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            transform: 'scale(1.05)',
          },
          transition: 'all 0.3s ease',
        }}
      >
        <Badge badgeContent={unread} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        disableScrollLock
        sx={{
          mt: 1,
          '& .MuiPaper-root': {
            backgroundColor: 'rgba(17, 17, 17, 0.97)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
            width: 380,
            maxWidth: '92vw',
            color: 'white',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 2,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>{t('notifications.title')}</Typography>
          {unread > 0 && (
            <Button
              size="small"
              onClick={handleMarkAllRead}
              sx={{ color: 'rgba(255,255,255,0.75)', textTransform: 'none', minWidth: 0 }}
            >
              {t('notifications.markAllRead')}
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} sx={{ color: 'white' }} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)' }}>
              {t('notifications.empty')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
            {notifications.map((notification) => (
              <Box
                key={notification.id}
                data-testid="notification-item"
                onClick={() => handleOpenNotification(notification)}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                  // Unread rows get a left marker rather than a different
                  // background, so the list stays readable at a glance.
                  borderLeft: notification.read_at
                    ? '3px solid transparent'
                    : '3px solid #C9A227',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.06)' },
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: notification.read_at ? 400 : 600 }}>
                  {notification.title}
                </Typography>
                {notification.body && (
                  <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
                    {notification.body}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
                  {new Date(notification.created_at).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationBell;
